import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import moment from "moment-timezone";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Select from "../../../components/Inputs/Select";
import { dateTimeFormat } from "../../../utils/helpers/date";
import {
	getSettingsOrgResourceSearchFilters,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";
import { renderUserName } from "../../../utils/helpers/user";
import { getUploadUrl } from "../../../utils/helpers/upload";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { useUser } from "../../../utils/hooks/user";
import { useAccess } from "../../../utils/hooks/access";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import StatusCell from "../Home/StatusCell";
import { canRequest } from "../permissions";
import { useOrgSettingsResourceLabel } from "../../../utils/hooks/useOrgSettingsResourceLabel";

const columnHelper = createColumnHelper();

export const useColumns = ({ approve, deny, cancel, view, namespace, accessMap }) => {
	const { t } = useTranslation();
	const { groups } = useGroups();
	const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();
	const dateFormat = useCompanyDateFormat();
	const timeFormat = useCompanyTimeFormat();

	const user = useUser();
	const { hasAccess: canManageUsers } = useAccess("canManageUsers");

	const columns = useMemo(() => {
		if (namespace === "faceRecognition") {
			const noop = () => {};
			return [
				columnHelper.accessor("user.employeeNumber", {
					enableColumnFilter: true,
					header: t("employee-number"),
				}),
				columnHelper.accessor("user", {
					enableColumnFilter: true,
					header: t("employee-name"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderUserName(value);
					},
				}),
				columnHelper.accessor("type", {
					header: t("type"),
					cell: (info) => t(info.getValue()),
				}),
				columnHelper.display({
					id: "verificationPhoto",
					enableColumnFilter: false,
					header: t("photo"),
					cell: ({ row: { original } }) => {
						const path = original?.data?.verificationPhoto;
						if (!path) {
							return null;
						}
						return (
							<img
								src={getUploadUrl(path)}
								alt={t("face-recognition")}
								style={{
									width: 40,
									height: 40,
									borderRadius: 6,
									objectFit: "cover",
								}}
							/>
						);
					},
				}),
				columnHelper.accessor("data.livenessPassed", {
					enableColumnFilter: false,
					header: t("liveness"),
					cell: ({ row: { original } }) => {
						const passed = original?.data?.livenessPassed;
						if (passed == null) {
							return null;
						}
						return passed ? t("yes") : t("no");
					},
				}),
				columnHelper.display({
					id: "submitted",
					enableColumnFilter: false,
					header: t("submitted"),
					cell: ({ row: { original } }) => {
						const date = original?.data?.lastRequestedAt || original?.createdAt;
						return date ? moment(date).format(dateFormat) : null;
					},
				}),
				columnHelper.accessor("status", {
					enableColumnFilter: true,
					header: t("status"),
					cell: ({ row }) => <StatusCell value={row.original} />,
					filterType: "select",
					Filter: (
						<Select>
							<Select.Option value="approved">{t("approved")}</Select.Option>
							<Select.Option value="denied">{t("denied")}</Select.Option>
							<Select.Option value="pending">{t("pending")}</Select.Option>
							<Select.Option value="expired">{t("expired")}</Select.Option>
						</Select>
					),
				}),
				columnHelper.display({
					id: "actions",
					enableHiding: false,
					enableColumnFilter: false,
					header: () => <div className="text-right">{t("actions")}</div>,
					cell: (info) => {
						const { id, status, requiredToBeApprovedBy } = info.row.original;
						const canViewRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canView", team: true });
						const canApproveRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canApprove", team: true }) || (requiredToBeApprovedBy || []).includes(user.id);
						const canDenyRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canDeny", team: true }) || (requiredToBeApprovedBy || []).includes(user.id);
						const approveAction = canApproveRow ? () => approve?.({ id, close: noop }) : null;
						const denyAction = canDenyRow ? () => deny?.({ id, close: noop }) : null;
						const viewAction = canViewRow ? () => view?.(info.row.original) : null;
						return (
							<div className={classNames("d-flex align-items-center justify-content-end gap-x-2")}>
								{viewAction && (
									<i
										className="fa-solid fa-eye text-dark cursor-pointer"
										onClick={viewAction}
										style={{ fontSize: "17px" }}
									/>
								)}
								{approveAction && (status === "pending" || status === "pending-canceled") && (
									<i
										className="fa-solid fa-check text-green cursor-pointer mx-2"
										onClick={approveAction}
										style={{ fontSize: "17px" }}
									/>
								)}
								{denyAction && (status === "pending" || status === "pending-canceled") && (
									<i
										className="fa-solid fa-xmark text-danger cursor-pointer"
										onClick={denyAction}
										style={{ fontSize: "17px" }}
									/>
								)}
							</div>
						);
					},
				}),
			];
		}

		const cols = [
			columnHelper.accessor("user.employeeNumber", {
				enableColumnFilter: true,
				header: t("employee-number"),
			}),
			columnHelper.accessor("user", {
				enableColumnFilter: true,
				header: t("employee-name"),
				cell: (info) => {
					const value = info.getValue();
					return value && renderUserName(value);
				},
			}),
		];

		if (namespace === "") {
			cols.push(
				columnHelper.accessor("date", {
					enableColumnFilter: false,
					header: t("date"),
					cell: ({ row: { original } }) => {
						const date = original?.data?.date || original?.data?.startDate;
						if (!date) {
							return null;
						}
						return moment(date).format(dateFormat);
					}
				}),
			);
		}

		if (namespace === "pto") {
			cols.push(
				columnHelper.accessor("data.allDay", {
					enableColumnFilter: true,
					header: t("all-day"),
					cell: (info) => info.getValue() ? t("yes") : t("no"),
				}),
			);
		}

		if (namespace !== "availability") {
			cols.push(
				columnHelper.accessor("startDate", {
					enableColumnFilter: false,
					header: t("start-time"),
					cell: ({ row: { original } }) => {
						// if (original.type !== "miss-punch") {
						// 	return null;
						// }
						const startDate = original.data?.startDate;
						if (!startDate) {
							return null;
						}
						if (namespace === "pto") {
							const { allDay } = original.data;
							if (allDay) {
								return dateTimeFormat(startDate, dateFormat);
							}
						}
						return `${dateTimeFormat(startDate, dateFormat)} ${dateTimeFormat(startDate, timeFormat)}`;
					}
				}),
				columnHelper.accessor("endDate", {
					enableColumnFilter: false,
					header: t("end-time"),
					cell: ({ row: { original } }) => {
						// if (original.type !== "miss-punch") {
						// 	return null;
						// }
						const endDate = original.data?.endDate;
						if (!endDate) {
							return null;
						}
						if (namespace === "pto") {
							const { allDay } = original.data;
							if (allDay) {
								return dateTimeFormat(endDate, dateFormat);
							}
						}
						return `${dateTimeFormat(endDate, dateFormat)} ${dateTimeFormat(endDate, timeFormat)}`;
					}
				}),
			);
		}

		if (namespace === "pto") {
			const startDateIndex = cols.findIndex(col => col.accessorKey === "data.allDay");
			cols.splice(
				startDateIndex,
				0,
				columnHelper.accessor("type", {
					enableColumnFilter: true,
					header: t("plan"),
					cell: ({ row }) => {
						const plan = row.original?.data?.plan?.plan;
						return plan && renderSettingsResourceLabel(plan);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/timeoff"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				}),
			)
		} else {
			cols.push(
				columnHelper.accessor("type", {
					header: t("type"),
					cell: (info) => t(info.getValue())
				})
			);
		}

		if (namespace !== "pto") {
			cols.push(
				columnHelper.accessor("data.location", {
					enableColumnFilter: true,
					header: t("location"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/locations"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				}),
				columnHelper.accessor("data.job", {
					enableColumnFilter: true,
					header: t("job"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/jobs"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				})
			);
		}

		if (groups) {
			const groupsColumns = groups.map((group) => {
				const level = group.level.replace("_", "");
				return columnHelper.accessor(level, {
					enableColumnFilter: true,
					header: group.description,
					cell: (info) => {
						const value = info.getValue();
						return value && renderOrgLevelResourceLabel(value);
					},
					filterType: "select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath={`/${group?.id}/org-levels`}
							mode="multiple"
							renderLabel={renderOrgLevelResourceLabel}
							getSearchFilters={
								getSettingsOrgResourceSearchFilters
							}
							valuePropName="id"
							hasSearch
						/>
					),
				});
			});
			cols.push(...groupsColumns);
		}

		cols.push(
			columnHelper.accessor("status", {
				enableColumnFilter: true,
				header: t("status"),
				cell: ({ row }) => <StatusCell value={row.original} />,
				filterType: "select",
				Filter: (
					<Select>
						<Select.Option value="approved">{t("approved")}</Select.Option>
						<Select.Option value="denied">{t("denied")}</Select.Option>
						<Select.Option value="pending">{t("pending")}</Select.Option>
						<Select.Option value="canceled">{t("canceled")}</Select.Option>
						<Select.Option value="expired">{t("expired")}</Select.Option>
					</Select>
				),
			})
		);

		// if (approve || deny || view) {
		const actionsColumn = columnHelper.display({
			id: "actions",
			enableHiding: false,
			enableColumnFilter: false,
			header: () => (
				<div className="text-right">
					{t("actions")}
				</div>
			),
			cell: (info) => {
				const { id, status, type, requiredToBeApprovedBy } = info.row.original;
				const noop = () => { };
				const isRequiredApprover = (requiredToBeApprovedBy || []).includes(user.id);
				// pending cancellations can only be resolved by user managers or the required approvers
				const isPendingCancel = status === "pending-canceled";
				const canViewRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canView", team: true });
				const canApproveRow = isPendingCancel
					? canManageUsers || isRequiredApprover
					: canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canApprove", team: true }) || isRequiredApprover;
				const canDenyRow = isPendingCancel
					? canManageUsers || isRequiredApprover
					: canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canDeny", team: true }) || isRequiredApprover;
				const canCancelRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canCancel", team: true });
				const approveAction = canApproveRow ? () => approve?.({ id, close: noop }) : null;
				const denyAction = canDenyRow ? () => deny?.({ id, close: noop }) : null;
				const viewAction = canViewRow ? () => view?.(info.row.original) : null;
				const cancelAction = (
					status === "approved"
					&& ["open-shift", "call-off", "direct-offer"].includes(type)
					&& canCancelRow
				) ? () => cancel?.(info.row.original) : null;
				return (
					<div
						className={classNames(
							"d-flex align-items-center justify-content-end gap-x-2",
						)}
					>
						{viewAction && (
							<i
								className="fa-solid fa-eye text-dark cursor-pointer"
								onClick={viewAction}
								style={{ fontSize: "17px" }}
							/>
						)}
						{approveAction &&
							(status === "pending" || status === "pending-canceled") && (
								<i
									className="fa-solid fa-check text-green cursor-pointer mx-2"
									onClick={approveAction}
									style={{ fontSize: "17px" }}
								/>
							)}
						{denyAction &&
							(status === "pending" || status === "pending-canceled") && (
								<i
									className="fa-solid fa-xmark text-danger cursor-pointer"
									onClick={denyAction}
									style={{ fontSize: "17px" }}
								/>
							)}
						{cancelAction && (
							<i
								className="fa-solid fa-ban text-primary cursor-pointer"
								onClick={cancelAction}
								title={t("cancel-request")}
								style={{ fontSize: "17px" }}
							/>
						)}
					</div>
				);
			},
		});
		cols.push(actionsColumn);
		// }

		return cols;
	}, [t, namespace, groups, dateFormat, timeFormat, user.id, view, approve, deny, cancel, accessMap, canManageUsers, renderOrgLevelResourceLabel]);

	return columns;
};
